/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import { focusSelectedChild, focusPreviousChild, focusNextChild } from "dc-preview-utils/src/Utils/FocusUtils";
import {
  quickToolClickHelper,
  resetFlyoutIndex,
  setIsFocusOverQuickTool,
  setIsClickedByMouse
} from "utils/QuickToolsUtils";
import { QuickToolItemContainer } from "containers/quickTools/QuickToolItemContainer";
import PrimaryPanelCss from "./PrimaryPanel.css";
import classNames from "classnames";

function renderQuickTools(verbGroups, quickToolsVerbs, shouldUseMobileModernViewer, activeVerb) {
  return (
    verbGroups.length > 0 &&
    verbGroups.map(verbGroup => {
      const { verbs, currentActiveVerb, Component } = verbGroup;
      // Filter verbs based on show property
      const verbDetails = verbs.filter(verb => quickToolsVerbs[verb].show).map(verb => quickToolsVerbs[verb]);
      const flyoutComponent = Component ? () => <Component /> : null;
      const isToolDisabled = quickToolsVerbs[currentActiveVerb].disabled;
      const disabledStateMsg = quickToolsVerbs[currentActiveVerb].disabledStateNotificationMsg;
      const groupIndex = quickToolsVerbs[currentActiveVerb].verbGroupIndex;
      // Show Caret if Flyout Component exists or there are more than 1 verbs
      const showCaret = !(verbDetails.length === 1 && !flyoutComponent);
      const quickToolData = { showCaret, verbDetails, currentActiveVerb, groupIndex };

      return currentActiveVerb && verbDetails.length > 0 ? (
        <li
          key={verbDetails[0].verbId}
          aria-current={currentActiveVerb === activeVerb ? "true" : "false"}
          data-test-id={`qt-${currentActiveVerb}`}
        >
          <QuickToolItemContainer
            verbDetails={verbDetails}
            currentActiveVerb={currentActiveVerb}
            flyoutComponent={flyoutComponent}
            isQuickToolDisabled={isToolDisabled}
            disabledStateNotificationMsg={disabledStateMsg}
            groupIndex={groupIndex}
            showCaret={showCaret}
            quickToolClickHelper={(evt, options) => quickToolClickHelper(evt, { ...options, ...quickToolData })}
            closeFlyout={() => {
              // must restore store state after closing flyout
              resetFlyoutIndex();
              setIsFocusOverQuickTool(false);
              setIsClickedByMouse(false);
            }}
            shouldUseMobileModernViewer={shouldUseMobileModernViewer}
          />
        </li>
      ) : null;
    })
  );
}

const PrimaryPanel = props => {
  const { activeVerb, filteredVerbGroups, quickToolsVerbs, shouldUseMobileModernViewer } = props;
  const elementHandle = useRef(null);
  const lastActiveVerb = useRef(null);

  const onKeyDown = event => {
    if (event.keyCode === codes.ARROW_UP) {
      focusPreviousChild(elementHandle.current, "button:not([disabled])", event, true);
    } else if (event.keyCode === codes.ARROW_DOWN) {
      focusNextChild(elementHandle.current, "button:not([disabled])", event, true);
    }
  };

  useEffect(() => {
    if (lastActiveVerb.current) {
      focusSelectedChild(elementHandle.current, "li");
    }
    lastActiveVerb.current = activeVerb;
  }, [activeVerb]);

  const primaryPanelTabListCss = shouldUseMobileModernViewer
    ? classNames(PrimaryPanelCss.PrimaryPanelTabList)
    : classNames(PrimaryPanelCss.PrimaryPanelTabList, PrimaryPanelCss.DesktopPrimaryPanelTabList);

  return (
    <ul ref={elementHandle} className={primaryPanelTabListCss} onKeyDown={onKeyDown}>
      {renderQuickTools(filteredVerbGroups, quickToolsVerbs, shouldUseMobileModernViewer, activeVerb)}
    </ul>
  );
};

PrimaryPanel.propTypes = {
  activeVerb: PropTypes.string,
  filteredVerbGroups: PropTypes.arrayOf(
    PropTypes.shape({
      verbs: PropTypes.arrayOf(PropTypes.string),
      currentActiveVerb: PropTypes.string.isRequired,
      Component: PropTypes.object
    })
  ),
  quickToolsVerbs: PropTypes.objectOf(PropTypes.object),
  shouldUseMobileModernViewer: PropTypes.bool
};

PrimaryPanel.defaultProps = {
  activeVerb: "",
  filteredVerbGroups: [
    {
      verbs: [],
      currentActiveVerb: "",
      Component: null
    }
  ],
  quickToolsVerbs: {},
  shouldUseMobileModernViewer: false
};

export default PrimaryPanel;
